/**
 * DİZİN RADARI — yayınlanan içeriklerin Google dizin durumu takibi.
 * Huni: Yayınlanan → Taranan → Dizinde (+ ortalama dizine girme günü).
 * Takvimde "Yayında" olan kartlar otomatik izlenir; elle URL de eklenebilir.
 * Kontroller kota dostu geri çekilme takvimiyle (1,2,3,5,7,10,14,21,30. günler) gece koşulur.
 */
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ScanSearch, Plus, RefreshCw, Trash2, CalendarDays, Globe, CircleCheck,
  CircleDashed, CircleAlert, Footprints, Clock, ChevronRight, ExternalLink,
} from "lucide-react";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { confirmDialog } from "@/components/confirm-dialog";
import { Jargon } from "@/components/ui/jargon";

type IndexCheck = {
  id: number;
  url: string;
  source: string;
  contentPlanId: number | null;
  publishedAt: string;
  status: "bekliyor" | "tarandi" | "dizinde" | "girmedi" | "dustu" | "hata";
  verdict: string | null;
  coverageState: string | null;
  googleCanonical: string | null;
  lastCrawlTime: string | null;
  lastmod: string | null;
  checkedAt: string | null;
  attempts: number;
  nextCheckAt: string | null;
  indexedAt: string | null;
  daysToIndex: number | null;
  note: string | null;
  /** Search Console URL Denetleme bağlantısı — "dizine eklenmesini iste" oradan yapılır. */
  denetleUrl: string | null;
};

/** Sunucudan açıklamalı gelir: ne anlama geliyor, neden oldu, ne yapılacak. */
type Sebep = {
  reason: string; n: number;
  etiket: string; anlam: string; neden: string; yapilacak: string;
  ciddiyet: "kritik" | "yuksek" | "orta" | "dusuk" | "bilgi";
  bizdeMi: boolean;
};

type RadarData = {
  checks: IndexCheck[];
  counts: Record<string, number>;
  reasons: Sebep[];
  avgDaysToIndex: number | null;
  gscConnected: boolean;
  scan: { running: boolean; done: number; total: number } | null;
};

// NOT: coverage_state çevirisi ve açıklaması artık SUNUCUDA (lib/index-durum.ts).
// Burada ikinci bir çeviri tablosu vardı; katalog büyüdükçe ikisi birbirinden
// ayrışacaktı. Tek kaynak sunucu, arayüz yalnız gösteriyor.

// Ciddiyet rozetleri — sebep listesi en acil olan üstte gelecek şekilde sunucuda sıralanır.
const CIDDIYET_AD: Record<Sebep["ciddiyet"], string> = {
  kritik: "ACİL", yuksek: "ÖNEMLİ", orta: "ORTA", dusuk: "DÜŞÜK", bilgi: "BİLGİ",
};
const CIDDIYET_STIL: Record<Sebep["ciddiyet"], string> = {
  kritik: "bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-400",
  yuksek: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
  orta: "bg-yellow-50 text-yellow-700 dark:bg-yellow-500/10 dark:text-yellow-400",
  dusuk: "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300",
  bilgi: "bg-gray-100 text-gray-500 dark:bg-white/5 dark:text-gray-400",
};

const API = "/api";

async function jfetch(path: string, init?: RequestInit): Promise<unknown> {
  const res = await fetch(`${API}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
  return body;
}

const STATUS_META: Record<IndexCheck["status"], { label: string; cls: string; icon: typeof CircleCheck }> = {
  bekliyor: { label: "Bekliyor", cls: "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300", icon: CircleDashed },
  tarandi: { label: "Tarandı, dizinde değil", cls: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300", icon: Footprints },
  dizinde: { label: "Dizinde", cls: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300", icon: CircleCheck },
  girmedi: { label: "Dizine girmedi", cls: "bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300", icon: CircleAlert },
  dustu: { label: "Dizinden düştü", cls: "bg-red-100 text-red-800 dark:bg-red-500/25 dark:text-red-200", icon: CircleAlert },
  hata: { label: "Hata", cls: "bg-orange-50 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300", icon: CircleAlert },
};

const fmtDate = (s: string | null): string =>
  s ? new Date(s).toLocaleDateString("tr-TR", { day: "numeric", month: "short" }) : "—";

export default function IndexRadarPage() {
  const { data: domains } = useListDomains();
  const [activeDomainId] = useActiveDomain(domains);
  const qc = useQueryClient();
  const [newUrl, setNewUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const [checkingId, setCheckingId] = useState<number | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [filter, setFilter] = useState<string | null>(null);
  const [reasonFilter, setReasonFilter] = useState<string | null>(null);
  const [acikSebep, setAcikSebep] = useState<string | null>(null);

  const { data, isLoading } = useQuery<RadarData>({
    queryKey: ["index-radar", activeDomainId, reasonFilter],
    queryFn: () => jfetch(`/domains/${activeDomainId}/index-radar${reasonFilter ? `?reason=${encodeURIComponent(reasonFilter)}` : ""}`) as Promise<RadarData>,
    enabled: !!activeDomainId,
    // Toplu analiz sürerken tablo canlı dolsun (4 sn), normalde dakikada bir
    refetchInterval: (q) => (q.state.data?.scan?.running ? 4_000 : 60_000),
  });

  const counts = data?.counts ?? {};
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const taranan = (counts["tarandi"] ?? 0) + (counts["dizinde"] ?? 0);
  const dizinde = counts["dizinde"] ?? 0;
  const pct = (n: number): string => (total > 0 ? `%${Math.round((n / total) * 100)}` : "—");

  const rows = useMemo(() => {
    const list = data?.checks ?? [];
    return filter ? list.filter((c) => c.status === filter) : list;
  }, [data, filter]);

  const say = (msg: string): void => {
    setFlash(msg);
    window.setTimeout(() => setFlash(null), 4000);
  };

  const addUrl = async (): Promise<void> => {
    const url = newUrl.trim();
    if (!url || !activeDomainId) return;
    setAdding(true);
    try {
      await jfetch(`/domains/${activeDomainId}/index-radar/track`, {
        method: "POST",
        body: JSON.stringify({ url }),
      });
      setNewUrl("");
      say("URL izlemeye alındı — ilk kontrol yarınki koşuda (ya da satırdan hemen denetleyin).");
      void qc.invalidateQueries({ queryKey: ["index-radar", activeDomainId] });
    } catch (e) {
      say((e as Error).message);
    } finally {
      setAdding(false);
    }
  };

  const checkNow = async (id: number): Promise<void> => {
    if (!activeDomainId) return;
    setCheckingId(id);
    try {
      await jfetch(`/domains/${activeDomainId}/index-radar/${id}/check`, { method: "POST" });
      void qc.invalidateQueries({ queryKey: ["index-radar", activeDomainId] });
    } catch (e) {
      say((e as Error).message);
    } finally {
      setCheckingId(null);
    }
  };

  const scanAll = async (): Promise<void> => {
    if (!activeDomainId) return;
    try {
      await jfetch(`/domains/${activeDomainId}/index-radar/scan-all`, { method: "POST" });
      say("Tüm site analizi başladı — tablo aşağıda canlı dolacak (büyük sitelerde birkaç dakika sürer).");
      void qc.invalidateQueries({ queryKey: ["index-radar", activeDomainId] });
    } catch (e) {
      say((e as Error).message);
    }
  };

  const removeRow = async (row: IndexCheck): Promise<void> => {
    const ok = await confirmDialog({
      title: "İzlemeden çıkar",
      description: `"${row.url}" dizin takibinden çıkarılsın mı?`,
      confirmText: "Çıkar",
      danger: true,
    });
    if (!ok || !activeDomainId) return;
    await jfetch(`/domains/${activeDomainId}/index-radar/${row.id}`, { method: "DELETE" });
    void qc.invalidateQueries({ queryKey: ["index-radar", activeDomainId] });
  };

  if (!activeDomainId) {
    return <div className="p-8 text-sm text-gray-500">Önce bir site seçin.</div>;
  }

  return (
    <div className="space-y-5 p-4 md:p-6">
      {/* GSC uyarısı */}
      {data && !data.gscConnected && (
        <div className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
          <CircleAlert className="h-4 w-4 shrink-0" />
          Dizin denetimi için Search Console bağlantısı gerekli. Bağlantı olmadan URL'ler sıraya alınır ama denetlenemez.
          <a href="/settings" className="ml-auto shrink-0 font-medium underline">Bağlantıyı yap</a>
        </div>
      )}

      {/* Huni kartları */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { key: null, label: "İzlenen URL", value: total, sub: "toplam", icon: Globe, tone: "text-gray-500" },
          { key: "tarandi", label: "Taranan", value: taranan, sub: pct(taranan), icon: Footprints, tone: "text-amber-500" },
          { key: "dizinde", label: "Dizinde", value: dizinde, sub: pct(dizinde), icon: CircleCheck, tone: "text-emerald-500" },
          { key: "__avg", label: "Ort. dizine girme", value: data?.avgDaysToIndex ?? "—", sub: "gün", icon: Clock, tone: "text-sky-500" },
        ].map((c) => (
          <button
            key={c.label}
            type="button"
            onClick={() => c.key !== "__avg" && setFilter(filter === c.key ? null : c.key)}
            className={`rounded-2xl border bg-white p-4 text-left transition dark:bg-white/[0.03] ${
              filter === c.key && c.key !== null
                ? "border-brand-500 ring-1 ring-brand-500/30"
                : "border-gray-200 dark:border-gray-800"
            } ${c.key === "__avg" ? "cursor-default" : "hover:border-gray-300 dark:hover:border-gray-700"}`}
          >
            <div className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400">
              <c.icon className={`h-4 w-4 ${c.tone}`} /> {c.label}
            </div>
            <div className="mt-2 text-2xl font-semibold text-gray-800 dark:text-white">{c.value}</div>
            <div className="text-xs text-gray-400">{c.sub}</div>
          </button>
        ))}
      </div>

      {/* GSC paritesi: Sayfalar neden dizine eklenmiyor? */}
      {(data?.reasons?.length ?? 0) > 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="border-b border-gray-100 px-5 py-4 dark:border-gray-800">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Sayfalar neden dizine eklenmiyor?</h3>
            <p className="mt-0.5 text-xs text-gray-400">
              Dizine eklenmeyen sayfalar Google'da sunulamaz — sebebe tıklayınca alttaki tablo o sayfalara filtrelenir.
            </p>
          </div>
          <div>
            {data!.reasons.map((r) => {
              const acik = acikSebep === r.reason;
              const secili = reasonFilter === r.reason;
              return (
                <div key={r.reason} className={`border-b border-gray-50 last:border-0 dark:border-gray-800/60 ${secili ? "bg-brand-50/70 dark:bg-brand-500/10" : ""}`}>
                  <div className="flex w-full items-center gap-2 px-5 py-3 text-sm">
                    <button
                      type="button"
                      onClick={() => setAcikSebep(acik ? null : r.reason)}
                      title="Bu ne demek?"
                      className="shrink-0 rounded p-0.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/10"
                    >
                      <ChevronRight className={`h-3.5 w-3.5 transition-transform ${acik ? "rotate-90" : ""}`} />
                    </button>
                    <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${CIDDIYET_STIL[r.ciddiyet]}`}>
                      {CIDDIYET_AD[r.ciddiyet]}
                    </span>
                    <button
                      type="button"
                      onClick={() => setReasonFilter(secili ? null : r.reason)}
                      title="Bu sebepteki sayfaları listele"
                      className={`flex-1 text-left ${secili ? "font-medium text-brand-600 dark:text-brand-300" : "text-gray-700 dark:text-gray-200"}`}
                    >
                      {r.etiket}
                    </button>
                    <span className="shrink-0 tabular-nums text-gray-500 dark:text-gray-400">{r.n}</span>
                  </div>
                  {/* DETAY: kullanıcı "Tarandı ama eklenmedi" yazısını okuyup ne yapacağını
                      bilmiyordu. "Keşfedildi" (hiç indirilmedi, sırada) ile "Tarandı"
                      (okundu, kalite kararıyla alınmadı) taban tabana zıt iş gerektiriyor. */}
                  {acik && (
                    <div className="space-y-1.5 border-t border-gray-50 bg-gray-50/60 px-5 py-3 text-xs dark:border-gray-800/60 dark:bg-white/[0.02]">
                      <p className="text-gray-700 dark:text-gray-200"><b>Ne oldu:</b> {r.anlam}</p>
                      <p className="text-gray-600 dark:text-gray-300"><b>Neden:</b> {r.neden}</p>
                      <p className="text-gray-800 dark:text-gray-100"><b>Ne yapmalı:</b> {r.yapilacak}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {reasonFilter && (
            <div className="border-t border-gray-100 px-5 py-2.5 dark:border-gray-800">
              <button type="button" onClick={() => setReasonFilter(null)} className="text-xs font-medium text-brand-500 hover:underline">
                Sebep filtresini temizle — tüm URL'leri göster
              </button>
            </div>
          )}
        </div>
      )}

      {/* Toplu analiz ilerlemesi */}
      {data?.scan?.running && (
        <div className="rounded-2xl border border-brand-200 bg-brand-50/60 px-4 py-3 dark:border-brand-500/30 dark:bg-brand-500/10">
          <div className="mb-1.5 flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 font-medium text-brand-600 dark:text-brand-300">
              <RefreshCw className="h-4 w-4 animate-spin" /> Tüm site analiz ediliyor…
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">{data.scan.done} / {data.scan.total} URL</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-white/10">
            <div
              className="h-full rounded-full bg-brand-500 transition-all"
              style={{ width: `${data.scan.total ? Math.round((data.scan.done / data.scan.total) * 100) : 0}%` }}
            />
          </div>
        </div>
      )}

      {/* Elle URL ekleme + toplu analiz + durum filtreleri */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => void scanAll()}
          disabled={!!data?.scan?.running || !data?.gscConnected}
          title={!data?.gscConnected ? "Search Console bağlantısı gerekli" : "Tarih beklemeden tüm bekleyen URL'leri şimdi denetle"}
          className="flex shrink-0 items-center gap-2 rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-50"
        >
          <ScanSearch className="h-4 w-4" />
          {data?.scan?.running ? "Analiz sürüyor…" : "Tüm Siteyi Analiz Et"}
        </button>
        {/* Dizinde OLMAYAN sayfaları Excel'e aktar (UTF-8 BOM'lu CSV — Excel doğrudan açar) */}
        <button
          type="button"
          onClick={() => {
            const disarida = (data?.checks ?? []).filter((c) => c.status !== "dizinde");
            if (!disarida.length) { say("Dizin dışında sayfa yok — aktarılacak satır bulunamadı."); return; }
            const hucre = (v: string | number | null | undefined) => `"${String(v ?? "").replace(/"/g, '""')}"`;
            const satirlar = [
              ["URL", "Durum", "Google Açıklaması", "Not", "Yayın Tarihi", "Son Kontrol", "Deneme", "Search Console Denetimi"],
              ...disarida.map((c) => [
                c.url, STATUS_META[c.status].label, c.coverageState ?? c.verdict ?? "", c.note ?? "",
                c.publishedAt?.slice(0, 10) ?? "", c.checkedAt?.slice(0, 10) ?? "", String(c.attempts), c.denetleUrl ?? "",
              ]),
            ].map((r) => r.map(hucre).join(";")).join("\r\n");
            // BOM olmadan Excel Türkçe karakterleri bozuyor; ";" ayracı TR bölge ayarıyla uyumlu
            const blob = new Blob(["﻿" + satirlar], { type: "text/csv;charset=utf-8" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `dizin-disi-sayfalar-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(a.href);
            say(`${disarida.length} dizin dışı sayfa Excel dosyası olarak indirildi.`);
          }}
          disabled={!(data?.checks ?? []).some((c) => c.status !== "dizinde")}
          title="Dizinde olmayan tüm sayfaları (bekleyen, taranmış ama girmemiş, düşmüş, hatalı) Excel'in doğrudan açtığı CSV olarak indirir."
          className="flex shrink-0 items-center gap-2 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-800 dark:text-gray-200 dark:hover:bg-white/[0.04]"
        >
          📥 Excel'e Aktar
        </button>
        <div className="flex min-w-[280px] flex-1 items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-gray-800 dark:bg-white/[0.03]">
          <Plus className="h-4 w-4 shrink-0 text-gray-400" />
          <input
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void addUrl()}
            placeholder="İzlemeye alınacak URL (https://…)"
            className="w-full bg-transparent text-sm text-gray-700 outline-none placeholder:text-gray-400 dark:text-gray-200"
          />
          <button
            type="button"
            onClick={() => void addUrl()}
            disabled={adding || !newUrl.trim()}
            className="shrink-0 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-600 disabled:opacity-50"
          >
            {adding ? "Ekleniyor…" : "İzlemeye Al"}
          </button>
        </div>
        {(["dustu", "bekliyor", "girmedi", "hata"] as const).map((s) => (
          (counts[s] ?? 0) > 0 && (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(filter === s ? null : s)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                filter === s ? "bg-brand-500 text-white" : STATUS_META[s].cls
              }`}
            >
              {STATUS_META[s].label} · {counts[s]}
            </button>
          )
        ))}
      </div>

      {flash && (
        <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-2.5 text-sm text-sky-800 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300">
          {flash}
        </div>
      )}

      {/* Tablo */}
      <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-left text-xs text-gray-400 dark:border-gray-800">
              <th className="px-4 py-3 font-medium">URL</th>
              <th className="px-4 py-3 font-medium">Yayın</th>
              <th className="px-4 py-3 font-medium">Son güncelleme</th>
              <th className="px-4 py-3 font-medium">Durum</th>
              <th className="px-4 py-3 font-medium">Dizine girme</th>
              <th className="px-4 py-3 font-medium" title="Search Console URL denetiminden gelir; satır denetlendikçe dolar">Google taraması</th>
              <th className="px-4 py-3 font-medium">Son kontrol</th>
              <th className="px-4 py-3 font-medium">Sonraki</th>
              <th className="px-4 py-3 font-medium text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-400">Yükleniyor…</td></tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center text-gray-400">
                  <ScanSearch className="mx-auto mb-2 h-8 w-8 opacity-40" />
                  {filter ? "Bu durumda URL yok." : (
                    <>Henüz izlenen URL yok. Sabah koşusunda sitemap'iniz taranıp tüm site otomatik izlemeye alınır;
                    takvimde <b>Yayında</b> yapılan kartların linki de anında düşer. Yukarıdan elle de ekleyebilirsiniz.</>
                  )}
                </td>
              </tr>
            )}
            {rows.map((row) => {
              const meta = STATUS_META[row.status] ?? STATUS_META.bekliyor;
              const Icon = meta.icon;
              return (
                <tr key={row.id} className="border-b border-gray-50 last:border-0 dark:border-gray-800/60">
                  <td className="max-w-[340px] px-4 py-3">
                    <a href={row.url} target="_blank" rel="noreferrer" className="block truncate text-gray-700 hover:text-brand-500 dark:text-gray-200" title={row.url}>
                      {row.url.replace(/^https?:\/\//, "")}
                    </a>
                    <div className="mt-0.5 flex items-center gap-2 text-[11px] text-gray-400">
                      {row.source === "takvim" && <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" /> Takvimden</span>}
                      {row.source === "manuel" && <span>Elle eklendi</span>}
                      {row.source === "sitemap" && <span>Sitemap</span>}
                      {row.note && <span className="truncate" title={row.note}>· {row.note}</span>}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-gray-500 dark:text-gray-400">
                    {/* Sitemap'ten lastmod'suz gelen olgun satırda yayın tarihi bilinmez — uydurma tarih gösterme */}
                    {row.source === "sitemap" && !row.lastmod ? "—" : fmtDate(row.publishedAt)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-gray-500 dark:text-gray-400" title={row.lastmod ? "Sitemap'teki lastmod değeri" : undefined}>
                    {fmtDate(row.lastmod)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${meta.cls}`} title={row.coverageState ?? undefined}>
                      <Icon className="h-3.5 w-3.5" /> {meta.label}
                    </span>
                    {row.googleCanonical && row.googleCanonical !== row.url && (
                      <div className="mt-1 max-w-[220px] truncate text-[11px] text-orange-500" title={`Google bu sayfa yerine şu adresi "asıl sayfa" kabul ediyor: ${row.googleCanonical}`}>
                        ⚠ Farklı <Jargon k="canonical" />
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-gray-500 dark:text-gray-400">
                    {row.status === "dizinde"
                      ? (row.daysToIndex !== null ? `${row.daysToIndex}. günde` : fmtDate(row.indexedAt))
                      : "—"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-gray-500 dark:text-gray-400" title={row.lastCrawlTime ? "Googlebot'un bu sayfayı son ziyareti (Search Console)" : "Satır denetlendiğinde Search Console'dan gelir"}>
                    {fmtDate(row.lastCrawlTime)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-gray-500 dark:text-gray-400">
                    {fmtDate(row.checkedAt)}
                    {row.attempts > 0 && <span className="ml-1 text-[11px] text-gray-400">({row.attempts}. kontrol)</span>}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-gray-500 dark:text-gray-400">
                    {row.nextCheckAt ?? "—"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => void checkNow(row.id)}
                        disabled={checkingId === row.id}
                        title={row.status === "dizinde" ? "Hâlâ dizinde mi? Şimdi doğrula" : "Şimdi kontrol et"}
                        className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-brand-500 disabled:opacity-50 dark:hover:bg-white/10"
                      >
                        <RefreshCw className={`h-4 w-4 ${checkingId === row.id ? "animate-spin" : ""}`} />
                      </button>
                      {/* GOOGLE'DA DENETLE — "dizine eklenmesini iste" butonu burada.
                          Google'ın bu işlem için açık bir API'si YOK (Indexing API
                          yalnız JobPosting/BroadcastEvent için), o yüzden isteği biz
                          gönderemiyoruz; kullanıcıyı doğru ekrana tek tıkla götürüyoruz. */}
                      {/* YAZILI buton: önce yalnız ↗ simgesi vardı ve kullanıcılar
                          ne işe yaradığını anlamıyordu. Etiket açıkça ne olacağını söyler. */}
                      {row.denetleUrl && row.status !== "dizinde" && (
                        <a
                          href={row.denetleUrl}
                          target="_blank"
                          rel="noreferrer"
                          title="Search Console'da açılır; oradaki 'Dizine eklenmesini iste' düğmesine basman gerekiyor. Google bu işlem için bize izin vermiyor, o yüzden sizin adınıza gönderemiyoruz."
                          className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-lg border border-brand-200 bg-brand-50 px-2 py-1 text-[11px] font-medium text-brand-600 transition hover:bg-brand-100 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300 dark:hover:bg-brand-500/20"
                        >
                          Google'a gönder
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => void removeRow(row)}
                        title="İzlemeden çıkar"
                        className="rounded-lg p-1.5 text-gray-400 transition hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400">
        Sitenin TÜM URL'leri sürekli izlenir: sitemap her sabah taranır, yeni yayınlar kendiliğinden takibe girer.
        Yeni içerik yayının 1, 2, 3, 5, 7, 10, 14, 21 ve 30. günlerinde denetlenir; dizindeki sayfalar ayda bir
        yeniden doğrulanır — dizinden düşen sayfa alarm üretir ve haftalık takibe alınır. Arama sonuçlarında gösterim
        alan URL'ler ek sorgu harcamadan otomatik "Dizinde" sayılır.
      </p>
      {/* DÜRÜSTLÜK: "neden otomatik dizine ekleme butonu yok" sorusunun cevabı.
          Google'ın Indexing API'si yalnız JobPosting/BroadcastEvent içindir; normal
          sayfalar için resmi bir yol yoktur. "Otomatik ekliyoruz" demek yalan olurdu. */}
      <p className="mt-2 px-1 text-xs leading-relaxed text-gray-400">
        <b>Dizine eklenmesini isteme:</b> Google'ın bunun için açık bir arayüzü (API) yoktur — Indexing API yalnız iş
        ilanı ve canlı yayın sayfaları içindir. Bu yüzden isteği sizin adınıza gönderemiyoruz. Satırdaki
        <ExternalLink className="mx-1 inline h-3 w-3 align-[-2px]" />
        simgesi sizi o URL'in Search Console denetim ekranına götürür; "Dizine eklenmesini iste" oradan tek tıkla
        yapılır. Not: bu istek dizine gireceğini garanti etmez, yalnız sırayı hızlandırır. "Tarandı ama eklenmedi"
        durumunda ise hiç işe yaramaz, çünkü Google sayfayı zaten görüp almamaya karar vermiştir.
      </p>
    </div>
  );
}
